import type { FamilyACommonParams } from '../types';
import { sampleBlueNoiseBilinear } from '../util/blue-noise-sample';
import { buildStampThreshold } from './stamp';

interface RegionOrigin {
  originX: number;
  originY: number;
  fullW: number;
  fullH: number;
  labelMap?: ImageData;
  label?: number;
}

type HalftoneParams = FamilyACommonParams & {
  /** Optional demo-carried noise blob size for Family A halftone jitter. */
  patternNoiseScale?: number;
};

/**
 * Family A - halftone dots renderer.
 *
 * "1 cell = 1 halftone dot": source averaged per cellSize × cellSize
 * cell, dot rendered at full output resolution with the chosen stamp
 * silhouette, blue-noise stochastic jitter applied at cell granularity
 * (one jitter value per dot, not per output pixel).
 *
 * The algorithm field on FamilyAMethod is intentionally ignored -
 * Family A has a single halftone strategy. Use Family C to reach the
 * v0.2.0 stamp-threshold renderer with its five algorithm variants.
 *
 * Solid-input correctness comes from a per-pixel blackThreshold /
 * whiteThreshold clamp against the FULL-resolution source. Solid black
 * stays solid black at any cellSize - line art keeps its edges.
 */
export function runFamilyAHalftone(
  source: ImageData,
  method: HalftoneParams,
  origin?: RegionOrigin,
): ImageData {
  const w = source.width;
  const h = source.height;
  // RegionOrigin lets the multi-region pipeline crop down to a region
  // bbox while keeping cell math aligned to absolute image coords.
  // Without it, a region that does not start at (0, 0) would land its
  // dots on a different sub-cell phase than a single-region run.
  const offX = origin?.originX ?? 0;
  const offY = origin?.originY ?? 0;

  const cellSize = Math.max(1, Math.floor(method.cellSize));
  const blackTh = method.blackThreshold ?? 0.02;
  const whiteTh = method.whiteThreshold ?? 0.98;
  const densityShift = (50 - method.density) / 50;
  const defaultJitter = method.stamp.type === 'pixel' ? 1 : 0.075;
  const jitterAmt = method.algorithmJitter ?? defaultJitter;
  const noiseScale = Math.max(1, method.patternNoiseScale ?? 1);
  const neighborRange = shouldSampleNeighborDots(method.stamp) ? 1 : 0;

  // Grid rotation is sampled in rotated space for stamp + jitter
  // coords (so the dot pattern rotates with the user's grid angle)
  // while the cell average stays axis-aligned (cellGray is downscaled
  // from the source as it is). An exact rotate-process-unrotate
  // pipeline would compute the cell average over the tilted footprint
  // too, at the cost of an O(N²) extra image rotation. For typical
  // halftone use the source is smooth at the cell scale, so the
  // approximation reads as the rotated grid the user expects.
  const fullW = origin?.fullW ?? w;
  const fullH = origin?.fullH ?? h;
  const gridRad = -((method.gridAngleDeg ?? 0) * Math.PI) / 180;
  const cosG = Math.cos(gridRad);
  const sinG = Math.sin(gridRad);
  const cx = fullW / 2;
  const cy = fullH / 2;
  const gridRotated = method.gridAngleDeg !== undefined && method.gridAngleDeg !== 0;

  const cellAverages = buildAxisAlignedCellAverages(source, cellSize, offX, offY, origin);

  const stampTh = buildStampThreshold(method.stamp, method.shapeAngleDeg ?? 0, {
    flipX: method.shapeFlipX ?? false,
    flipY: method.shapeFlipY ?? false,
  });

  const fgValue = method.polarity === 'black' ? 0 : 255;
  const bgValue = method.polarity === 'black' ? 255 : 0;
  const polarityWhite = method.polarity === 'white';

  const out = new ImageData(w, h);
  const dst = out.data;
  const src = source.data;

  for (let py = 0; py < h; py++) {
    const fullPy = offY + py;
    for (let px = 0; px < w; px++) {
      const fullPx = offX + px;
      const i = (py * w + px) * 4;
      const srcGray = (src[i] ?? 0) / 255;

      let v: number;
      if (srcGray <= blackTh) {
        v = 0; // solid-dark = pure black regardless of polarity
      } else if (srcGray >= whiteTh) {
        v = 255; // solid-bright = pure white regardless of polarity
      } else {
        // Stamp + jitter coords: rotated around the full-image centre
        // when gridAngleDeg ≠ 0, so the dot pattern itself rotates.
        let stampX: number;
        let stampY: number;
        if (gridRotated) {
          const dx = fullPx - cx;
          const dy = fullPy - cy;
          stampX = dx * cosG - dy * sinG + cx;
          stampY = dx * sinG + dy * cosG + cy;
        } else {
          stampX = fullPx;
          stampY = fullPy;
        }
        const baseCellX = Math.floor(stampX / cellSize);
        const baseCellY = Math.floor(stampY / cellSize);

        let paint = false;
        for (let dy = -neighborRange; dy <= neighborRange && !paint; dy++) {
          for (let dx = -neighborRange; dx <= neighborRange; dx++) {
            const dotCellX = baseCellX + dx;
            const dotCellY = baseCellY + dy;
            const u = (stampX - dotCellX * cellSize + 0.5) / cellSize;
            const uv = (stampY - dotCellY * cellSize + 0.5) / cellSize;
            const stampVal = stampTh(u, uv);
            if (stampVal <= 0) continue;

            const avgCell = averageCellForDot(
              dotCellX,
              dotCellY,
              cellSize,
              gridRotated,
              cosG,
              sinG,
              cx,
              cy,
            );
            const adjustedAvg = clamp01(
              cellAverages.averageAt(avgCell.x, avgCell.y) + densityShift,
            );
            const foregroundInk = polarityWhite ? adjustedAvg : 1 - adjustedAvg;

            // Blue-noise jitter sampled at CELL coords - one
            // perturbation per dot, not one per output pixel. This is
            // what makes the output read as a halftone instead of a
            // per-pixel screentone.
            const bn =
              (sampleBlueNoiseBilinear(dotCellX, dotCellY, noiseScale) + 0.5) / 256 - 0.5;
            const cutoff = 1 - foregroundInk - jitterAmt * bn;
            if (stampVal > cutoff) {
              paint = true;
              break;
            }
          }
        }

        v = paint ? fgValue : bgValue;
      }

      dst[i] = v;
      dst[i + 1] = v;
      dst[i + 2] = v;
      dst[i + 3] = 255;
    }
  }

  return out;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function shouldSampleNeighborDots(stamp: FamilyACommonParams['stamp']): boolean {
  return (
    stamp.coverage > 1 &&
    stamp.type !== 'line' &&
    stamp.type !== 'cross' &&
    stamp.type !== 'asterisk'
  );
}

function averageCellForDot(
  dotCellX: number,
  dotCellY: number,
  cellSize: number,
  gridRotated: boolean,
  cosG: number,
  sinG: number,
  cx: number,
  cy: number,
): { x: number; y: number } {
  if (!gridRotated) return { x: dotCellX, y: dotCellY };

  const stampCenterX = (dotCellX + 0.5) * cellSize;
  const stampCenterY = (dotCellY + 0.5) * cellSize;
  const dx = stampCenterX - cx;
  const dy = stampCenterY - cy;
  const sourceCenterX = dx * cosG + dy * sinG + cx;
  const sourceCenterY = -dx * sinG + dy * cosG + cy;
  return {
    x: Math.floor(sourceCenterX / cellSize),
    y: Math.floor(sourceCenterY / cellSize),
  };
}

interface CellAverageLookup {
  averageAt(cellX: number, cellY: number): number;
}

function buildAxisAlignedCellAverages(
  source: ImageData,
  cellSize: number,
  offX: number,
  offY: number,
  origin: RegionOrigin | undefined,
): CellAverageLookup {
  const w = source.width;
  const h = source.height;
  const startCellX = Math.floor(offX / cellSize);
  const startCellY = Math.floor(offY / cellSize);
  const endCellX = Math.floor((offX + Math.max(0, w - 1)) / cellSize);
  const endCellY = Math.floor((offY + Math.max(0, h - 1)) / cellSize);
  const cellsW = Math.max(1, endCellX - startCellX + 1);
  const cellsH = Math.max(1, endCellY - startCellY + 1);
  const sums = new Float32Array(cellsW * cellsH);
  const counts = new Uint32Array(cellsW * cellsH);
  const src = source.data;
  const labelMap = origin?.labelMap;
  const targetLabel = origin?.label;
  const labelData = labelMap?.data;
  const fullW = origin?.fullW ?? w;

  for (let y = 0; y < h; y++) {
    const fullY = offY + y;
    const cellY = Math.floor(fullY / cellSize) - startCellY;
    for (let x = 0; x < w; x++) {
      const fullX = offX + x;
      if (labelData !== undefined && targetLabel !== undefined) {
        const labelIdx = (fullY * fullW + fullX) * 4;
        if (labelData[labelIdx] !== targetLabel) continue;
      }

      const cellX = Math.floor(fullX / cellSize) - startCellX;
      const idx = cellY * cellsW + cellX;
      sums[idx]! += src[(y * w + x) * 4] ?? 0;
      counts[idx] = counts[idx]! + 1;
    }
  }

  return {
    averageAt(cellX: number, cellY: number): number {
      const localX = Math.min(cellsW - 1, Math.max(0, cellX - startCellX));
      const localY = Math.min(cellsH - 1, Math.max(0, cellY - startCellY));
      const idx = localY * cellsW + localX;
      const count = counts[idx]!;
      return count === 0 ? 0 : sums[idx]! / count / 255;
    },
  };
}
